import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { httpClient } from '../services/httpClient';
import { ENDPOINTS } from '../config/api.config';
import '../styles/admin.css';

const AdminEditService = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Data lists
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [allTags, setAllTags] = useState([]);

  // Form states
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [workingDays, setWorkingDays] = useState([]);
  const [timeRange, setTimeRange] = useState({ start: '09:00', end: '18:00' });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    user_id: '',
  });

  const daysList = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [srvRes, cats, vens, tgs] = await Promise.all([
        // Don't use cache for service details - always get fresh data
        httpClient.get(`${ENDPOINTS.ADMIN_API}/services/${id}`, null, { useCache: false }),
        httpClient.get(ENDPOINTS.CATEGORIES, null, { useCache: true }),
        httpClient.get(`${ENDPOINTS.ADMIN_API}/vendors?per_page=500`, null, { useCache: true }),
        httpClient.get(ENDPOINTS.TAGS, null, { useCache: true }),
      ]);

      // Handle response (httpClient returns data directly, no wrapper)
      const srv = srvRes?.id ? srvRes : (srvRes?.data?.id ? srvRes.data : (srvRes?.data?.data || srvRes?.data));
      
      if (!srv || !srv.id) {
        setError('Failed to load service data - invalid response');
        setLoading(false);
        return;
      }

      const categoriesData = Array.isArray(cats) ? cats : (Array.isArray(cats?.data) ? cats.data : []);
      const vendorsData = Array.isArray(vens) ? vens : (Array.isArray(vens?.data) ? vens.data : (Array.isArray(vens?.data?.data) ? vens.data.data : []));
      const tagsData = Array.isArray(tgs) ? tgs : (Array.isArray(tgs?.data) ? tgs.data : []);

      setCategories(categoriesData);
      setVendors(vendorsData);
      setAllTags(tagsData);

      // Set form data
      setFormData({
        name: srv.name,
        description: srv.description,
        price: srv.price,
        category_id: srv.category_id,
        user_id: srv.user_id,
      });

      // Parse working hours
      if (srv.hours) {
        parseHours(srv.hours);
      }

      // Load tags for this service
      if (srv.tags && Array.isArray(srv.tags)) {
        setSelectedTags(srv.tags);
      }
    } catch (err) {
      setError('Failed to load service data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const parseHours = (hoursString) => {
    try {
      const match = hoursString.match(/(.+?)\s*\((\d{2}:\d{2})-(\d{2}:\d{2})\)/);
      if (match) {
        const daysStr = match[1];
        const start = match[2];
        const end = match[3];
        const days = daysStr.split(',').map(d => d.trim());
        setWorkingDays(days);
        setTimeRange({ start, end });
      }
    } catch (err) {
      console.error('Error parsing hours:', err);
    }
  };

  // --- Tag filtering effect ---
  useEffect(() => {
    if (tagSearch.trim().length > 0) {
      const search = tagSearch.toLowerCase();
      const tagsArray = Array.isArray(allTags) ? allTags : [];
      const filtered = tagsArray.filter(tag =>
        tag && tag.name && tag.name.toLowerCase().includes(search) &&
        !selectedTags.some(selected => selected && selected.id === tag.id)
      );
      setFilteredTags(filtered.slice(0, 8));
    } else {
      setFilteredTags([]);
    }
  }, [tagSearch, allTags, selectedTags]);

  const toggleDay = (day) => {
    setWorkingDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleTagSelect = (tag) => {
    setSelectedTags(prev => [...prev, tag]);
    setTagSearch('');
    setFilteredTags([]);
  };

  const handleTagRemove = (tag) => {
    setSelectedTags(prev => prev.filter(t => t.id !== tag.id));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const tagName = tagSearch.trim();
      if (tagName && !selectedTags.some(t => t.name.toLowerCase() === tagName.toLowerCase())) {
        const existingTag = allTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        if (existingTag) {
          handleTagSelect(existingTag);
        } else {
          const newTag = { id: null, name: tagName, temp: true };
          setSelectedTags(prev => [...prev, newTag]);
          setTagSearch('');
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_id) {
      setError('Please select a vendor');
      return;
    }

    setSubmitting(true);
    const hoursFormatted = workingDays.length > 0
      ? `${workingDays.join(', ')} (${timeRange.start}-${timeRange.end})`
      : 'Closed / Appointment Only';

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: formData.price,
        category_id: parseInt(formData.category_id),
        user_id: parseInt(formData.user_id),
        hours: hoursFormatted,
        tags: selectedTags,
      };

      await httpClient.put(`${ENDPOINTS.ADMIN_API}/services/${id}`, payload);
      
      // Clear cache for services list to ensure fresh data
      httpClient.clearCache();
      
      alert('Service updated successfully!');
      navigate('/admin', { state: { refreshServices: true } });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update service');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="admin-section" style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  return (
    <div className="section" style={{ maxWidth: '720px' }}>
      <style>{`
        .day-pill { padding: 6px 12px; border: 1px solid #e5e7eb; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; transition: 0.2s; background: #f9fafb; color: #1f2937; }
        .day-pill.active { background: #3b82f6; color: white; border-color: #3b82f6; }
        .tag-pill { padding: 6px 14px; border-radius: 20px; border: 1px solid #e5e7eb; cursor: pointer; font-size: 12px; transition: 0.2s; background: #eff6ff; color: #1f2937; }
        .tag-pill.active { background: #3b82f6; color: white; border-color: #3b82f6; }
        .suggestion-box { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px; max-height: 200px; overflow-y: auto; margin-bottom: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
        .suggestion-item { padding: 10px 12px; cursor: pointer; border-bottom: 1px solid #f3f4f6; font-size: 13px; transition: 0.15s; background: #ffffff; color: #1f2937; }
        .suggestion-item:hover { background: #f9fafb; color: #3b82f6; font-weight: 500; }
        .suggestion-item:last-child { border-bottom: none; }
        .step-circle { flex: none; width: 28px; height: 28px; border-radius: 50%; background: #3b82f6; display: flex; align-items: center; justify-content: center; color: white; font-size: 0.75rem; font-weight: 700; }
        .form-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 16px; padding: 24px; margin-bottom: 24px; }
        .form-input, .form-select, .form-textarea { background: #ffffff; border: 1px solid #d1d5db; color: #1f2937; padding: 10px 12px; border-radius: 8px; font-family: inherit; font-size: 14px; }
        .form-input::placeholder, .form-select::placeholder { color: #9ca3af; }
        .form-input:focus, .form-select:focus, .form-textarea:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); background: #ffffff; }
        .form-input:disabled { background: #f3f4f6; color: #6b7280; }
        .form-label { display: block; margin-bottom: 6px; font-weight: 600; color: #1f2937; font-size: 0.9rem; }
      `}</style>

      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#1f2937' }}>✏️ Edit Service</h1>
        <p style={{ color: '#6b7280' }}>Update service details and information</p>
      </div>

      <form onSubmit={handleSubmit}>

        {/* SECTION 1: Category & Basic Info */}
        <div className="form-card">
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div className="step-circle">1</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1f2937' }}>Service Details</div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Update service information</div>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>Vendor *</label>
            <select className="form-select" value={formData.user_id} onChange={e => setFormData({ ...formData, user_id: e.target.value })} required>
              <option value="">Select a Vendor</option>
              {vendors.map(vendor => (
                <option key={vendor.id} value={vendor.id}>{vendor.name} ({vendor.business_name || 'N/A'})</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>Category *</label>
            <select className="form-select" value={formData.category_id} onChange={e => setFormData({ ...formData, category_id: e.target.value })} required>
              <option value="">Select Category</option>
              {categories.filter(cat => !cat.parent_id).map((cat) => (
                <optgroup key={cat.id} label={cat.name}>
                  {categories.filter(c => c.parent_id === cat.id).map(child => (
                    <option key={child.id} value={child.id}>{child.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>Service Name *</label>
              <input type="text" className="form-input" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>Price *</label>
              <input type="number" className="form-input" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} required />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>Description *</label>
            <textarea className="form-textarea" rows="4" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required></textarea>
          </div>
        </div>

        {/* SECTION 2: Working Hours */}
        <div className="form-card">
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div className="step-circle">2</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1f2937' }}>Working Hours</div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Update availability</div>
            </div>
          </div>

          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>Select Working Days</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
            {daysList.map(day => (
              <div key={day} className={`day-pill ${workingDays.includes(day) ? 'active' : ''}`} onClick={() => toggleDay(day)}>
                {day}
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>Opens At</label>
              <input type="time" className="form-input" value={timeRange.start} onChange={e => setTimeRange({ ...timeRange, start: e.target.value })} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>Closes At</label>
              <input type="time" className="form-input" value={timeRange.end} onChange={e => setTimeRange({ ...timeRange, end: e.target.value })} />
            </div>
          </div>
        </div>

        {/* SECTION 3: Tags */}
        <div className="form-card">
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div className="step-circle">3</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1f2937' }}>Search Keywords</div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Help users find this service</div>
            </div>
          </div>

          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>Tags (Optional - Press Enter to add)</label>
          <input
            type="text"
            className="form-input"
            placeholder="Search or type a tag and press Enter..."
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            onKeyDown={handleTagInputKeyDown}
            style={{ marginBottom: '10px' }}
          />
          
          {filteredTags.length > 0 && (
            <div className="suggestion-box">
              {filteredTags.map(tag => (
                <div key={tag.id} onClick={() => handleTagSelect(tag)} className="suggestion-item">
                  {tag.name}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {selectedTags.map(tag => (
              <div key={tag.id || tag.name} className="tag-pill active">
                # {tag.name}
                <span
                  onClick={() => handleTagRemove(tag)}
                  style={{ marginLeft: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  ✕
                </span>
              </div>
            ))}
          </div>
        </div>

        {error && <div style={{ color: '#ef4444', marginBottom: '15px', textAlign: 'center', padding: '12px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '12px', marginTop: '24px', marginBottom: '40px' }}>
          <button className="btn btn-primary" style={{flex: 1, padding: '14px', fontSize: '1rem'}} type="submit" disabled={submitting}>
            {submitting ? '⏳ Updating...' : '✓ Update Service'}
          </button>
          <button type="button" className="btn btn-secondary" style={{flex: 0.5, padding: '14px'}} onClick={() => navigate('/admin/services')}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default AdminEditService;